import { LoadingSpinner as BaseLoadingSpinner, FullPageError } from "./common/components";
import { BookingData, BookingTool, Service } from "./AppDataProvider/AppDataProvider";
import { useClientLocation } from "./utils";
import React, { ReactElement } from "react";
import { absoluteCentre } from "./common/styles";
import { ContentContainer } from "./ContentContainer";
import styled from "styled-components";
import { GetBookingToolScopedServicesQuery, GetBookingToolSettingsQuery, GetOrganisationDetailsQuery, useGetBookingToolDataQuery, useGetBookingToolScopedServicesQuery, useGetBookingToolSettingsQuery, useGetOrganisationDetailsQuery } from "./GeneratedGraphQL";
type BookingToolDataProps = {
  inPopoverMode?: boolean;
};

/**
 * Entry point for a "real" (non-generic) booking tool embed — i.e. the
 * widget was loaded with a `?id=<bookingToolId>` URL param.
 *
 * large multi-location booking tools were loading every location's
 * full data via `GetBookingToolData` on every single page load, even when
 * the page already knows which one organisation it's for via the
 * `?organisationId=` URL param (used for their per-store pages). That
 * query fetches `services(first: 500)` + `organisations(first: 300)` and
 * auto-paginates until everything is loaded, producing the ~2.3MB/3.3s
 * payload reported in that ticket.
 *
 * When `organisationId` is present we can't yet know whether it's safe to
 * fetch only that one organisation — that depends on
 * `organisationAvailabilityFlow`, a booking-tool-level setting we don't
 * have until we ask the server. Rather than paying that lookup cost for
 * every booking tool, we only do it when `organisationId` is present at
 * all (see `BookingToolSettingsGate`); every other embed (no
 * `organisationId`, i.e. the vast majority of traffic) renders exactly as
 * before via `FullBookingToolData`, with zero behavioural change.
 */
export function BookingToolData({
  inPopoverMode
}: BookingToolDataProps): ReactElement {
  const urlParams = new URLSearchParams(window.location.search);
  const prefilledOrganisationId = urlParams.get("organisationId");
  if (prefilledOrganisationId) {
    return <BookingToolSettingsGate inPopoverMode={inPopoverMode} prefilledOrganisationId={prefilledOrganisationId} />;
  }
  return <FullBookingToolData inPopoverMode={inPopoverMode} data-sentry-element="FullBookingToolData" data-sentry-component="BookingToolData" data-sentry-source-file="BookingToolData.tsx" />;
}
type BookingToolSettingsGateProps = BookingToolDataProps & {
  prefilledOrganisationId: string;
};

/**
 * Fetches only the scalar/settings fields of the booking tool (via the
 * services-free `GetBookingToolSettings`) so we can decide whether it's
 * safe to take the scoped-organisation fast path below, without paying for
 * a `services(first: 500)` fetch — ours or `GetBookingToolData`'s — just to
 * find that out.
 *
 * The booking-tool-scoped services for the fast path are fetched
 * separately, by `ScopedBookingToolDataLoader`, only once
 * `organisationAvailabilityFlow` is confirmed true here — see
 * `GetBookingToolScopedServices` in Queries.graphql and
 * https://github.com/appointedd/booking-tools/pull/19 for why that query
 * sources from `BookingTool.services` rather than `Organisation.services`.
 */
function BookingToolSettingsGate({
  inPopoverMode,
  prefilledOrganisationId
}: BookingToolSettingsGateProps): ReactElement {
  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get("id") ?? "";
  const {
    data,
    loading,
    error
  } = useGetBookingToolSettingsQuery({
    variables: {
      id
    }
  });
  if (loading) return <LoadingSpinnerContainer>
        <LoadingSpinner />
      </LoadingSpinnerContainer>;
  if (error || !data?.bookingTool) return <FullPageError />;

  /**
   * organisationAvailabilityFlow is, today, the only booking tool
   * configuration where we've confirmed that
   * nothing downstream needs data for any organisation other than the
   * prefilled one. Any other booking tool configuration falls back to the
   * existing, full-fetch behaviour, completely untouched.
   */
  if (data.bookingTool.organisationAvailabilityFlow) {
    return <ScopedBookingToolDataLoader bookingToolSettings={data.bookingTool} inPopoverMode={inPopoverMode} prefilledOrganisationId={prefilledOrganisationId} />;
  }
  return <FullBookingToolData inPopoverMode={inPopoverMode} data-sentry-element="FullBookingToolData" data-sentry-component="BookingToolSettingsGate" data-sentry-source-file="BookingToolData.tsx" />;
}
type ScopedBookingToolDataLoaderProps = BookingToolDataProps & {
  bookingToolSettings: NonNullable<GetBookingToolSettingsQuery["bookingTool"]>;
  prefilledOrganisationId: string;
};

/**
 * Fires the booking-tool-scoped services fetch (`GetBookingToolScopedServices`)
 * and the organisation-detail fetch (`GetOrganisationDetails`) in the same
 * render, so the two requests go out in parallel instead of one blocking
 * the other.
 */
function ScopedBookingToolDataLoader({
  bookingToolSettings,
  inPopoverMode,
  prefilledOrganisationId
}: ScopedBookingToolDataLoaderProps): ReactElement {
  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get("id") ?? "";
  const preFillResourceId = urlParams.get("resourceId");
  const {
    data: servicesData,
    loading: servicesLoading,
    error: servicesError,
    fetchMore
  } = useGetBookingToolScopedServicesQuery({
    variables: {
      id,
      serviceFilters: {
        organisationIds: [prefilledOrganisationId],
        ...(preFillResourceId && {
          resourceIds: [preFillResourceId]
        }),
        showInvisibleIfOnlyInvisibleSelected: true,
        visibleOnly: true
      }
    }
  });
  const {
    data: organisationData,
    loading: organisationLoading,
    error: organisationError
  } = useGetOrganisationDetailsQuery({
    variables: {
      id: prefilledOrganisationId
    }
  });
  const services = servicesData?.bookingTool?.services;
  if (services?.pageInfo.hasNextPage) {
    // omit serviceFilters so Apollo keeps the original query's visibility filters on every page
    void fetchMore({
      variables: {
        id,
        serviceCursor: services.pageInfo.endCursor
      }
    });
  }

  /**
   * It's possible we are still fetching the next page of this
   * organisation's booking-tool-scoped services (in practice this is
   * rare for this flow — scoping to a single organisationId plus the
   * booking tool's own restrictions means there's almost always only
   * one service — but we keep the same defensive handling as the other
   * data-loading components), or still waiting on the organisation details.
   */
  if (servicesLoading || organisationLoading || services?.pageInfo.hasNextPage) return <LoadingSpinnerContainer>
        <LoadingSpinner />
      </LoadingSpinnerContainer>;
  if (servicesError || !services || organisationError || !organisationData?.organisation) return <FullPageError />;

  // organisationId is valid but not assigned to this booking tool — fall back to the membership-checked full flow
  if (services.edges.length === 0) {
    return <FullBookingToolData inPopoverMode={inPopoverMode} />;
  }
  return <ScopedBookingToolData bookingToolSettings={bookingToolSettings} inPopoverMode={inPopoverMode} organisation={organisationData.organisation} services={services} data-sentry-element="ScopedBookingToolData" data-sentry-component="ScopedBookingToolDataLoader" data-sentry-source-file="BookingToolData.tsx" />;
}
type ScopedBookingToolDataProps = BookingToolDataProps & {
  bookingToolSettings: NonNullable<GetBookingToolSettingsQuery["bookingTool"]>;
  organisation: NonNullable<GetOrganisationDetailsQuery["organisation"]>;
  services: NonNullable<GetBookingToolScopedServicesQuery["bookingTool"]>["services"];
};

/**
 * Renders the scoped-organisation fast path from the already-loaded data
 * (see `ScopedBookingToolDataLoader`), wrapping it into the same
 * `BookingTool` shape `ContentContainer`/`Steps` already expect — the same
 * adapter technique `GenericBookingToolData` uses, just populated from real
 * booking-tool settings instead of hardcoded generic defaults, and with a
 * real `_id` so downstream steps (availability, payments, etc.) that
 * branch on `isGenericBookingTool` correctly treat this as a real booking
 * tool rather than the fully-generic organisationId-only embed.
 */
function ScopedBookingToolData({
  bookingToolSettings,
  inPopoverMode,
  organisation,
  services
}: ScopedBookingToolDataProps): ReactElement {
  const {
    location,
    timezone: clientTimezone
  } = useClientLocation({
    fallbackLocation: "GB",
    fallbackTimezone: "Europe/London"
  });
  const urlParams = new URLSearchParams(window.location.search);
  const createdSource = urlParams.get("createdSource");
  let bookingData: BookingData = {
    createdSource: createdSource ? createdSource : "bookingapp",
    organisation,
    sessionId: Math.random().toString(12).substring(2, 9),
    timezone: organisation.forceTimezone === "AUTO_DETECT" ? clientTimezone ?? "Europe/London" : organisation.forceTimezone
  };

  /**
   * If there is only 1 service we need to use the id of that single
   * service. This will be required when the service selection step is
   * disabled. For this flow there is always exactly one
   * service per organisation.
   *
   * Also, if the service only allows one attendee, or only has one
   * duration, we also need to add those values directly to the
   * bookingData.
   */
  if (services.edges.length === 1) {
    const service = services.edges[0].node as Service;
    const {
      durations,
      maxParticipantsPerBooking
    } = service;
    bookingData = {
      ...bookingData,
      service,
      ...(durations.length === 1 && {
        duration: durations[0]
      }),
      ...((maxParticipantsPerBooking ?? 1) === 1 && {
        attendees: 1
      })
    };
  }
  const bookingTool: BookingTool = {
    ...bookingToolSettings,
    isGenericBookingTool: false,
    organisations: {
      edges: [{
        cursor: organisation._id,
        node: organisation
      }],
      pageInfo: {
        endCursor: null,
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: null,
        total: 1
      }
    },
    services
  };
  return <ContentContainer bookingData={bookingData} bookingTool={bookingTool} inPopoverMode={inPopoverMode} userLocation={location} data-sentry-element="ContentContainer" data-sentry-component="ScopedBookingToolData" data-sentry-source-file="BookingToolData.tsx" />;
}

/**
 * The original, unscoped fetch-everything-up-front behaviour, unchanged.
 * Used for every booking tool embed that either has no prefilled
 * `organisationId`, or does but doesn't have `organisationAvailabilityFlow`
 * enabled.
 */
function FullBookingToolData({
  inPopoverMode
}: BookingToolDataProps): ReactElement {
  const {
    location,
    timezone: clientTimezone
  } = useClientLocation({
    fallbackLocation: "GB",
    fallbackTimezone: "Europe/London"
  });
  const urlParams = new URLSearchParams(window.location.search);
  const id = urlParams.get("id") ?? "";
  const preFillResourceId = urlParams.get("resourceId");
  const createdSource = urlParams.get("createdSource");
  const prefilledOrganisationId = urlParams.get("organisationId");
  const {
    data,
    loading,
    error,
    fetchMore
  } = useGetBookingToolDataQuery({
    variables: {
      id,
      serviceFilters: {
        ...(preFillResourceId && {
          resourceIds: [preFillResourceId]
        }),
        /**
         * We want to show invisible services for constructed booking tools that
         * have _only_ invisible services assigned to them.
         */
        showInvisibleIfOnlyInvisibleSelected: true,
        visibleOnly: true
      }
    }
  });
  const services = data?.bookingTool?.services;
  const organisations = data?.bookingTool?.organisations;
  const forceServiceSelection = data?.bookingTool?.forceServiceSelection;
  const organisationAvailabilityFlow = data?.bookingTool?.organisationAvailabilityFlow;
  if (loading) return <LoadingSpinnerContainer>
        <LoadingSpinner />
      </LoadingSpinnerContainer>;
  if (error || !data?.bookingTool || !organisations?.edges.length) return <FullPageError />;
  if (services && (services.pageInfo.hasNextPage || organisations.pageInfo.hasNextPage)) {
    void fetchMore({
      variables: {
        id,
        ...(organisations.pageInfo.hasNextPage && {
          organisationCursor: organisations.pageInfo.endCursor
        }),
        ...(services.pageInfo.hasNextPage && {
          serviceCursor: services.pageInfo.endCursor
        })
      }
    });
  }

  /**
   * It's possible we are still fetching the next pages
   * of the organisations & services graphs.
   *
   * If that is the case return the loading content
   */
  if (services && (services.pageInfo.hasNextPage || organisations.pageInfo.hasNextPage)) {
    return <LoadingSpinnerContainer>
        <LoadingSpinner />
      </LoadingSpinnerContainer>;
  }

  /**
   * There is a scenario where timezone.name can be undefined: Sentry issue https://appointedd-eu.sentry.io/issues/1474580/
   *
   * For now, adding a fallback timezone to fix this in case of undefined
   */

  let bookingData: BookingData = {
    createdSource: createdSource ? createdSource : "bookingapp",
    sessionId: Math.random().toString(12).substring(2, 9),
    timezone: clientTimezone ?? "Europe/London"
  };

  // If there is only 1 organisation we need to automatically select this organisation
  if (organisations.edges.length === 1) {
    const organisation = organisations.edges[0].node;
    bookingData = {
      ...bookingData,
      organisation: organisation,
      ...(organisation.forceTimezone !== "AUTO_DETECT" && {
        timezone: organisation.forceTimezone
      })
    };
  }
  const prefilledOrganisation = prefilledOrganisationId && organisations.edges ? organisations.edges.find(({
    node
  }) => node._id === prefilledOrganisationId) : null;
  if (prefilledOrganisation) {
    bookingData = {
      ...bookingData,
      organisation: prefilledOrganisation.node,
      ...(prefilledOrganisation.node.forceTimezone !== "AUTO_DETECT" && {
        timezone: prefilledOrganisation.node.forceTimezone
      })
    };
  }

  /**
   * Set organisation and service: when there is a prefilled organisation, set the organisation to that pre-filled organisation
   * Filter services to find ones that include that prefilled organisation; then take the first service (for this flow there is only one service per organisation)
   * This IF block will contain the GF feature flag that will be replaced with a booking tool feature flag later
   *
   * NOTE: in practice, booking tools with organisationAvailabilityFlow
   * enabled no longer reach this component at all when organisationId is
   * prefilled — see BookingToolSettingsGate/ScopedBookingToolData above —
   * so this block is now effectively dead for this flow specifically. It's
   * left in place as a safety net for any other booking tool that might
   * have this flag set without us being aware of it, rather than removed outright.
   */
  if (prefilledOrganisationId && prefilledOrganisation && organisationAvailabilityFlow && services && services.edges.length > 0) {
    const organisationServices = services.edges.filter(({
      node
    }) => node.organisation._id === prefilledOrganisationId);
    const service = organisationServices[0].node as Service;
    const {
      durations,
      maxParticipantsPerBooking
    } = service;
    bookingData = {
      ...bookingData,
      organisation: prefilledOrganisation.node,
      service,
      ...(durations.length === 1 && {
        duration: durations[0]
      }),
      ...((maxParticipantsPerBooking ?? 1) === 1 && {
        attendees: 1
      })
    };
  }

  /**
   * If there is only 1 service we need to use the id of that single service.
   * This will be required when the service selection step is disabled.
   *
   * Also, if the service only allows one attendee, or only has one duration,
   * we also need to add those values directly to the bookingData.
   */
  if (services && services.edges.length === 1 && !forceServiceSelection) {
    const service = services.edges[0].node as Service;
    const {
      durations,
      maxParticipantsPerBooking
    } = service;
    bookingData = {
      ...bookingData,
      service,
      ...(durations.length === 1 && {
        duration: durations[0]
      }),
      ...((maxParticipantsPerBooking ?? 1) === 1 && {
        attendees: 1
      })
    };
  }
  return <ContentContainer bookingData={bookingData} bookingTool={data.bookingTool} inPopoverMode={inPopoverMode} userLocation={location} data-sentry-element="ContentContainer" data-sentry-component="FullBookingToolData" data-sentry-source-file="BookingToolData.tsx" />;
}
const LoadingSpinner = styled(BaseLoadingSpinner)`
  ${absoluteCentre}
`;
const LoadingSpinnerContainer = styled.section`
  min-height: 550px;
`;
export default BookingToolData;